import { Download, WifiOff } from "lucide-react";
import { useEffect, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useRegisterSW } from "virtual:pwa-register/react";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/analytics/analytics";
import { canInstall, install, needsIosHint, subscribeInstall } from "@/lib/pwa/install";

/** A new version was downloaded: offer to reload (never reloads on its own mid-edit). */
export function PwaUpdate() {
  const { t } = useTranslation();
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    // Long-lived tabs and installed apps check for a new version every hour.
    onRegisteredSW: (_url, registration) => {
      if (registration) setInterval(() => void registration.update(), 60 * 60 * 1000);
    },
  });

  useEffect(() => {
    if (!needRefresh) return;
    toast(t("pwa.updateReady"), {
      duration: Infinity,
      action: { label: t("pwa.reload"), onClick: () => void updateServiceWorker(true) },
      onDismiss: () => setNeedRefresh(false),
    });
  }, [needRefresh, setNeedRefresh, t, updateServiceWorker]);

  return null;
}

const subscribeOnline = (listener: () => void) => {
  window.addEventListener("online", listener);
  window.addEventListener("offline", listener);
  return () => {
    window.removeEventListener("online", listener);
    window.removeEventListener("offline", listener);
  };
};

/** A thin bar under the header while the device is offline. */
export function OfflineBanner() {
  const { t } = useTranslation();
  const online = useSyncExternalStore(
    subscribeOnline,
    () => navigator.onLine,
    () => true,
  );
  if (online) return null;
  return (
    <div
      role="status"
      className="flex items-center justify-center gap-2 bg-muted px-4 py-1.5 text-xs text-muted-foreground"
      data-testid="offline-banner"
    >
      <WifiOff className="size-3.5" aria-hidden /> {t("pwa.offline")}
    </div>
  );
}

/** "Install app" where the browser allows it, or how to do it on iOS; nothing once installed. */
export function InstallApp() {
  const { t } = useTranslation();
  const available = useSyncExternalStore(subscribeInstall, canInstall, () => false);
  if (available) {
    return (
      <Button
        variant="outline"
        className="w-full justify-start"
        onClick={() => void install().then((ok) => ok && track("app_installed"))}
        data-testid="install-app"
      >
        <Download /> {t("pwa.install")}
      </Button>
    );
  }
  if (needsIosHint()) {
    return <p className="px-1 text-sm text-muted-foreground">{t("pwa.iosHint")}</p>;
  }
  return null;
}
